import { useRef, useState } from 'react';

import classnames from 'classnames';
import AvatarEditor from 'react-avatar-editor';
import { FormProvider, useForm } from 'react-hook-form';

import { useTranslation } from '@/i18n';

import { ModalContent, ModalFooter } from '../../global/modal/ModalContent';
import { ModalProps } from '../../global/modal/types';
import Button from '../Button';
import Icon from '../Icon';
import { useModals } from '../modal/ModalManagerProvider';

import styles from './index.module.scss';

type EditAvatarModalProps = {
  onSave: (file: File) => void;
  onClose?: () => void;
  existingImageUrl?: string;
} & ModalProps;

export default function EditAvatarModal({ onSave, onClose, existingImageUrl }: EditAvatarModalProps) {
  const { t } = useTranslation('common');
  const [image, setImage] = useState<File | string | null>(existingImageUrl || null);
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const editorRef = useRef<AvatarEditor>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { closeModal } = useModals();

  const form = useForm();

  const handleModalDismiss = () => {
    onClose && onClose();
    closeModal();
  };

  const handleSave = () => {
    if (editorRef.current) {
      const canvas = editorRef.current.getImageScaledToCanvas();
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const file = new File([blob], 'avatar.png', { type: 'image/png' });
            onSave(file);
            handleModalDismiss();
            closeModal();
          }
        },
        'image/png',
        1,
      );
    }
  };

  const handleUploadClick = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImage(e.target.files[0]);
    }
  };

  const incrementZoom = () => {
    setScale((prev) => Math.min(prev + 0.1, 3));
  };

  const decrementZoom = () => {
    setScale((prev) => Math.max(prev - 0.1, 1));
  };

  const rotateLeft = () => {
    setRotate((prev) => (prev - 15) % 360);
  };

  const rotateRight = () => {
    setRotate((prev) => (prev + 15) % 360);
  };

  return (
    <FormProvider {...form}>
      <form className="d-flex flex-column overflow-auto">
        <ModalContent>
          {image ? (
            <>
              <div className={classnames('text-center')}>
                <AvatarEditor
                  border={50}
                  borderRadius={500}
                  color={[255, 255, 255, 0.6]}
                  crossOrigin="anonymous"
                  height={250}
                  image={image}
                  ref={editorRef}
                  rotate={rotate}
                  scale={scale}
                  width={250}
                />
              </div>
              <div className={classnames(styles.changeImageButtonContainer, 'text-center')}>
                <Button onClick={handleUploadClick} text={t('imageUpload.changeImage')} type="secondary" />
                <input accept="image/*" onChange={handleFileChange} ref={inputRef} style={{ display: 'none' }} type="file" />
              </div>
              <div className={classnames(styles.controlsWrapper)}>
                <div className={classnames(styles.controlContainer)}>
                  <Icon icon="search-minus" onClick={decrementZoom} size={'lg'} />
                  <input
                    className={styles.controlSlider}
                    id="zoom-slider"
                    max="3"
                    min="1"
                    onChange={(e) => setScale(parseFloat(e.target.value))}
                    step="0.1"
                    type="range"
                    value={scale}
                  />
                  <Icon icon="search-plus" onClick={incrementZoom} size={'lg'} />
                </div>
                <div className={classnames(styles.controlContainer)}>
                  <Icon icon="undo" onClick={rotateLeft} size={'lg'} />
                  <input
                    className={styles.controlSlider}
                    id="rotate-slider"
                    max="360"
                    min="0"
                    onChange={(e) => setRotate(parseInt(e.target.value, 10))}
                    step="1"
                    type="range"
                    value={rotate}
                  />
                  <Icon icon="redo" onClick={rotateRight} size={'lg'} />
                </div>
              </div>
            </>
          ) : (
            <div className={classnames(styles.uploadButtonContainer, 'text-center')}>
              <Button onClick={handleUploadClick} text={t('imageUpload.uploadImage')} type="success" />
              <input
                accept="image/*"
                id="avatarUploadInput"
                onChange={handleFileChange}
                ref={inputRef}
                style={{ display: 'none' }}
                type="file"
              />
            </div>
          )}
        </ModalContent>

        <ModalFooter>
          <Button onClick={handleModalDismiss} text={t('btnCancel')} type="secondary" />
          <Button disabled={!image} onClick={handleSave} text={t('btnSave')} type="success" />
        </ModalFooter>
      </form>
    </FormProvider>
  );
}
